<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useHeader } from '@/cms-ui/composables/useHeader'
import { useBrxmMenu } from '@/cms-ui/composables/useBrxmMenu'
import { useBrxmData } from '@/cms-ui/composables/useBrxmData'
import { EClickElement } from '@/cms-ui/utils/dataLayer'
import { VIconButtonVariants } from '@/ui/plushComponents/VIconButton/types'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()

const {
    handleHeaderOnScroll,
    handleHeaderOnResize,
    handleBlanket,
    isMobileMenuOpen,
    isDesktopMenuOpen,
    isHeaderHideOnScrollingDown,
} = useHeader()

const isPreview = computed(() => props.page.isPreview())

// const menu = computed(() => {
//     const menuRef = props.component.getModels()?.menu
//     const menu = menuRef && props.page.getContent(menuRef)

//     return isMenu(menu) ? menu : undefined
// })

const items = await useBrxmMenu(props)
const data = useBrxmData(props, 'HEADER')

const { logo, cart, cta } = data.value

onMounted(() => {
    useEventListener(window, 'scroll', handleHeaderOnScroll)
    useEventListener(window, 'resize', handleHeaderOnResize)
})

function toggleMobileMenu(): void {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
}
</script>

<template>
    <VBlanket
        :is-open="isMobileMenuOpen || isDesktopMenuOpen"
        data-test="blanket-header"
        @click="handleBlanket"
    />

    <div class="h-[72px] lg:h-[84px]" />

    <header
        class="lg:pt-base lg:pl-base lg:pr-base space-header fixed left-1/2 top-0 z-50 flex w-full max-w-[1472px] -translate-x-1/2 justify-center transition-transform duration-500"
        :class="{
            '-translate-y-full lg:translate-y-[calc(-100%-16px)]': isHeaderHideOnScrollingDown,
        }"
    >
        <!-- <BrManageMenuButton :menu="menu" /> -->
        <div
            class="shadow-default-below bg-brand-accent rounded-b-container-lg py-sm lg:px-base lg:rounded-container-md relative flex min-h-[68px] w-full flex-col justify-center overflow-hidden transition-all duration-300 ease-linear lg:py-[6px]"
            :class="{
                'bg-comp-primary-dark': isDesktopMenuOpen,
                'has-edit-button': isPreview,
            }"
        >
            <div class="container grid grid-cols-4 lg:grid-cols-12">
                <div class="col-span-1 flex items-center">
                    <a
                        :href="logo.url"
                        @click="
                            useClickTracking({
                                label: 'logo',
                                clickPath: '/',
                                clickElement: EClickElement.ICON,
                                componentName: 'header',
                                componentCategory: 'menu',
                            })
                        "
                    >
                        <img
                            src="../../assets/images/logo-plush.svg"
                            alt="Logo PLUSH"
                        />
                    </a>
                </div>

                <div class="col-span-3 flex justify-end lg:col-span-11 lg:justify-between">
                    <HeaderPartsHeaderNavDesktop
                        class="hidden lg:block"
                        :items="items"
                    />

                    <div class="lg:gap-sm flex items-center justify-end">
                        <VIconButton
                            class="hidden min-w-[48px] lg:flex"
                            :variant="VIconButtonVariants.plain"
                            size="MEDIUM"
                            icon="call"
                            :dark="isDesktopMenuOpen"
                            data-test="header-call"
                            href="https://kpirrc18nap4j36bdffnbaqsychvk87b.plushbezlimitu.pl/kontakt"
                        />

                        <HeaderPartsBasketButton
                            class="z-20"
                            :is-desktop-menu-open="isDesktopMenuOpen"
                            :cart="cart"
                        />

                        <BaseButton
                            class="lg:hidden"
                            variant="secondary"
                            label="button"
                            size="MEDIUM"
                            :aria-expanded="isMobileMenuOpen"
                            aria-controls="mobile-menu"
                            data-test="mobile-menu"
                            @click="[
                                toggleMobileMenu(),
                                useClickTracking({
                                    label: 'hamburger menu',
                                    clickPath: '',
                                    clickElement: EClickElement.BUTTON,
                                    componentName: 'header',
                                    componentCategory: 'menu',
                                }),
                            ]"
                        >
                            <div class="flex items-center">
                                MENU
                                <HeaderPartsHamburgerIcon
                                    class="ml-2"
                                    :is-open="isMobileMenuOpen"
                                />
                            </div>
                        </BaseButton>

                        <BaseButton
                            class="relative z-10 hidden lg:flex"
                            variant="primary"
                            label="button"
                            size="SMALL"
                            data-test="header-desktop-cta"
                            :href="cta.url"
                            @click="
                                useClickTracking({
                                    label: 'przejdź do plusha',
                                    clickPath: cta.url,
                                    clickElement: EClickElement.BUTTON,
                                    componentName: 'header',
                                    componentCategory: 'menu',
                                })
                            "
                        >
                            PRZEJDŹ DO PLUSHA
                        </BaseButton>
                    </div>
                </div>
            </div>

            <HeaderPartsHeaderNavMobile
                :items="items"
                :is-open="isMobileMenuOpen"
                :cta-href="cta.url"
            />

            <HeaderPartsHeaderNavDrawerDesktop
                :items="items"
                :is-open="isDesktopMenuOpen"
            />

            <transition name="fade">
                <img
                    v-if="isDesktopMenuOpen"
                    src="../../assets/images/plush-bear.png"
                    alt="PLUSH"
                    aria-label="PLUSH"
                    class="absolute bottom-0 right-0 z-0 hidden lg:block"
                />
            </transition>
        </div>
    </header>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
